/**
 * =============================================================================
 * ROASTBREW - PLAYLIST SAMPLE DATA (temporary)
 * =============================================================================
 *
 * Stand-in for the playlist server, which does not exist yet. Every playlist
 * page reads from HERE and nowhere else, so replacing this module with real API
 * calls later touches one file, not four pages.
 *
 * SHAPE — chosen to match CONTRACT.md's principles:
 *   Playlist { code, title, author, visibility: 'private' | 'public', description, items: [Item] }
 *   Item     { type: 'lesson', videoId, title, course, section, length }
 *          | { type: 'note', id, text }
 *
 * When this becomes real:
 *   - a lesson item will store only `videoId`; its title, course and thumbnail come
 *     from the catalog (findTopicInCatalog in VideoViewPage shows the lookup), and
 *     lock state from isLocked(topic.tierRequired, currentTier) — never stored here.
 *   - "watched" will come from progressByVideo (data/progress.js), never stored here.
 *     The `SAMPLE_WATCHED` set below exists only so the pages look right today.
 *   - `length` will come from the video detail (video.durationSeconds).
 *
 * URL addresses use the playlist `code` (12 digits), shown to people as 4-4-4.
 * =============================================================================
 */

// ── Lessons a playlist can draw from (sample) ───────────────────────────────
// Mirrors catalog topics. `videoId`s here are sample ids, not real catalog ids.
export const SAMPLE_LESSONS = [
  { videoId: 'sample-lim1', title: 'What a limit really means', course: 'Calculus', section: 'Limits', length: '7:12' },
  { videoId: 'sample-lim2', title: 'Limits from graphs and tables', course: 'Calculus', section: 'Limits', length: '8:40' },
  { videoId: 'sample-der1', title: 'The derivative as a slope', course: 'Calculus', section: 'Derivatives', length: '9:05' },
  { videoId: 'sample-der2', title: 'Power, product and quotient rules', course: 'Calculus', section: 'Derivatives', length: '11:22' },
  { videoId: 'sample-der3', title: 'The chain rule', course: 'Calculus', section: 'Derivatives', length: '10:48' },
  { videoId: 'sample-int1', title: 'Areas under curves', course: 'Calculus', section: 'Integrals', length: '9:31' },
  { videoId: 'sample-int2', title: 'The fundamental theorem', course: 'Calculus', section: 'Integrals', length: '12:04' },
  { videoId: 'sample-alg1', title: 'Two-step equations', course: 'Algebra 1', section: 'Equations', length: '6:55' },
  { videoId: 'sample-alg2', title: 'Slope-intercept form', course: 'Algebra 1', section: 'Linear functions', length: '9:02' },
  { videoId: 'sample-alg3', title: 'Solving by factoring', course: 'Algebra 1', section: 'Quadratics', length: '8:20' },
  { videoId: 'sample-geo1', title: 'Similar triangles', course: 'Geometry', section: 'Similarity', length: '8:52' },
  { videoId: 'sample-sat1', title: 'Reading data from tables', course: 'SAT', section: 'Problem solving', length: '6:20' },
];
const lesson = (videoId) => ({ type: 'lesson', ...SAMPLE_LESSONS.find((l) => l.videoId === videoId) });

// ── Playlists (sample) ──────────────────────────────────────────────────────
export const SAMPLE_PLAYLISTS = {
  '126837053612': {
    code: '126837053612',
    title: 'Calculus in ten hours',
    author: 'Ms. Rivera',
    visibility: 'public',
    description: 'Limits, derivatives and integrals in order, one short lesson at a time.',
    items: [
      lesson('sample-lim1'),
      lesson('sample-lim2'),
      { type: 'note', id: 'note-1', text: 'Before the derivative lessons, take five minutes to review slope from Algebra 1. We will start Thursday with a short quiz on it.' },
      lesson('sample-der1'),
      lesson('sample-der2'),
      lesson('sample-der3'),
      lesson('sample-int1'),
      lesson('sample-int2'),
    ],
  },
  '480215596731': {
    code: '480215596731',
    title: 'Exam week review',
    author: 'You',
    visibility: 'private',
    description: 'Your picks for the algebra final.',
    items: [lesson('sample-alg1'), lesson('sample-alg2'), lesson('sample-alg3'), lesson('sample-sat1')],
  },
};

// Sample "watched" state — ONLY so the pages look right today. Real progress
// comes from progressByVideo.
export const SAMPLE_WATCHED = new Set(['sample-lim1', 'sample-lim2']);

// ── The /playlist home page rows (sample) ───────────────────────────────────
// Real versions: "continue" + "recent" from viewing history, "saved" from the
// Save button, "suggested" from the recommendation rules.
// `count` is the playlist's lesson count; `done` how many are watched.
export const SAMPLE_HOME = {
  continue: { code: '126837053612', item: 4 },   // item 4 = "The derivative as a slope" (item 3 is the note)
  recent: [
    { code: '480215596731', title: 'Exam week review', author: 'You', count: 8, done: 5, firstLesson: 'Two-step equations', course: 'Algebra 1' },
    { code: '553019274468', title: 'Linear equations bootcamp', author: 'Ana M.', count: 16, done: 11, firstLesson: 'One-step equations', course: 'Algebra 1' },
    { code: '771406385210', title: 'SAT math sprint', author: 'Coach Daniel', count: 18, done: 4, firstLesson: 'Linear equations, fast', course: 'SAT' },
    { code: '209847166053', title: 'Geometry midterm', author: 'Priya S.', count: 11, done: 9, firstLesson: 'Angle pairs', course: 'Geometry' },
  ],
  saved: [
    { code: '384920571106', title: 'Trig without tears', author: 'Ms. Rivera', count: 13, when: 'Saved yesterday', firstLesson: 'Angles on the unit circle', course: 'Trigonometry' },
    { code: '615073829447', title: 'Probability puzzles', author: 'Leo T.', count: 8, when: 'Saved last week', firstLesson: 'Counting outcomes', course: 'Statistics' },
    { code: '902716648301', title: 'Factoring, made simple', author: 'Mr. Okafor', count: 10, when: 'Saved Sep 18', firstLesson: 'Greatest common factor', course: 'Algebra 1' },
    { code: '147392018856', title: 'Physics for beginners', author: 'Dr. Chen', count: 14, when: 'Saved Sep 4', firstLesson: 'Speed and velocity', course: 'Physics' },
  ],
  suggested: [
    { code: '638105274902', title: 'AP Calculus AB review', author: 'Ms. Rivera', count: 22, why: 'Because you watched “Calculus in ten hours”', firstLesson: 'Limits, quickly', course: 'Calculus' },
    { code: '290481736615', title: 'Integrals, step by step', author: 'Dr. Chen', count: 12, why: 'Because you watched “Calculus in ten hours”', firstLesson: 'Riemann sums', course: 'Calculus' },
    { code: '815560243179', title: 'Systems of equations', author: 'Priya S.', count: 12, why: 'Popular with Algebra 1 students', firstLesson: 'Graphing two lines', course: 'Algebra 1' },
    { code: '462937108854', title: 'Statistics basics', author: 'Leo T.', count: 10, why: 'New this week', firstLesson: 'Mean, median and mode', course: 'Statistics' },
  ],
};

// ── Helpers shared by the pages ─────────────────────────────────────────────

// The playlist at a code, or null.
// Sample only: the two full playlists above, plus any card on the /playlist home
// page — those get a stand-in playlist built from the card (its title and author,
// with sample lessons from the same course) so every card opens something.
// Any other code is null, which the edit page treats as a brand-new playlist.
export function getSamplePlaylist(code) {
  const key = String(code || '');
  if (SAMPLE_PLAYLISTS[key]) return SAMPLE_PLAYLISTS[key];
  const card = [...SAMPLE_HOME.recent, ...SAMPLE_HOME.saved, ...SAMPLE_HOME.suggested].find((c) => c.code === key);
  if (!card) return null;
  const sameCourse = SAMPLE_LESSONS.filter((l) => l.course === card.course);
  const pool = sameCourse.length ? sameCourse : SAMPLE_LESSONS;
  return {
    code: key,
    title: card.title,
    author: card.author,
    visibility: 'public',
    description: '',
    items: pool.slice(0, 6).map((l) => ({ type: 'lesson', ...l })),
  };
}

// A fresh 12-digit code that never starts with 0. Real codes come from the server,
// which must also check the code is not already taken.
export function newPlaylistCode() {
  let s = String(1 + Math.floor(Math.random() * 9));
  for (let i = 0; i < 11; i++) s += Math.floor(Math.random() * 10);
  return s;
}

// "126837053612" → ["1268", "3705", "3612"] (shown with faint dashes between).
export function codeGroups(code) {
  const s = String(code || '').replace(/\D/g, '');
  return [s.slice(0, 4), s.slice(4, 8), s.slice(8, 12)].filter(Boolean);
}

// Lesson numbering skips notes: returns each item with `lessonNo` (lessons only).
export function numberItems(items) {
  let n = 0;
  return (items || []).map((it) => (it.type === 'lesson' ? { ...it, lessonNo: ++n } : { ...it, lessonNo: null }));
}

// "9:05" → 545 seconds.
export function lengthToSeconds(t) {
  const [m, s] = String(t || '0:00').split(':').map(Number);
  return (m || 0) * 60 + (s || 0);
}

// 2355 seconds → "39 min" / "1 h 9 min".
export function formatMinutes(sec) {
  const m = Math.round(sec / 60);
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
}

// A pool of REAL thumbnails from the catalog (the same approach the old
// PlaylistsPage used), so sample cards show genuine video images. Empty when the
// catalog has not loaded; cards then fall back to a plain title card.
export function catalogThumbnails(catalog) {
  const out = [];
  (catalog || []).forEach((s) => (s.courses || []).forEach((c) => (c.chapters || []).forEach((ch) =>
    (ch.sections || []).forEach((sec) => (sec.topics || []).forEach((t) => { if (t.thumbnailUrl) out.push(t.thumbnailUrl); })))));
  return out;
}
